// ============================================================================
// Decks (spec §10.5.3) — the learner's saved sets: a Favorites deck (★ from any
// study space) plus custom decks. List decks, open one to see resolved items,
// remove items, delete a custom deck, and launch a focused practice session
// scoped to the deck (#/study/deck/<id>).
// ============================================================================

import { el, mount, toast } from '../dom.js';
import * as api from '@core/data/api.js';
import { ITEM_TYPE } from '@core/models/constants.js';

export function renderDecks({ navigate }) {
  const container = el('div.container.stack');
  mount(container, el('div.card', { text: 'Loading decks…' }));

  const openDeckId = (location.hash || '').split('/')[2] || null; // #/decks or #/decks/<id>

  function deckRow(d) {
    return el('button.card.browse-card', { onclick: () => navigate(`#/decks/${d.id}`) }, [
      el('div.browse-card__glyph', { text: d.kind === 'favorite' ? '★' : '📚' }),
      el('div.stack', { style: 'gap:2px' }, [
        el('strong', { text: d.name }),
        el('div.faint', { text: `${d.item_count} item${d.item_count === 1 ? '' : 's'}${d.kind === 'favorite' ? ' · favorites' : ''}` }),
      ]),
    ]);
  }

  async function loadList() {
    try {
      const decks = await api.myDecks();
      const newName = el('input.input', { placeholder: 'New deck name…', maxlength: 60 });
      const create = el('button.btn', {
        text: 'Create', onclick: async () => {
          const n = newName.value.trim();
          if (!n) return;
          try { await api.deckCreate(n); toast('Deck created', 'success'); loadList(); }
          catch (e) { toast(e.message || 'Could not create deck', 'error'); }
        },
      });
      mount(container,
        el('div.row', {}, [el('h1', { text: 'Decks' }), el('div.appbar__spacer'),
          el('button.btn.btn--ghost', { text: '← Home', onclick: () => navigate('#/') })]),
        el('div.card.stack', {}, [
          el('p.muted', { text: 'Save Hanja and words into decks — your Favorites (★ from any study space) or custom decks — then study a deck as a focused set.' }),
          el('div.row', { style: 'gap:var(--space-2)' }, [newName, create]),
        ]),
        decks.length
          ? el('div.stack', {}, decks.map(deckRow))
          : el('div.card', {}, [el('p.muted', { text: 'No decks yet. Create one above, or ★ an item in its study space to start your Favorites.' })]),
      );
    } catch (e) {
      mount(container, el('div.card.banner.banner--warn', { text: e.message || 'Could not load decks' }));
    }
  }

  async function loadDeck(id) {
    try {
      const [decks, items] = await Promise.all([api.myDecks(), api.deckContents(id)]);
      const deck = decks.find((d) => d.id === id);

      const itemCard = (it) => {
        const isChar = it.item_type === ITEM_TYPE.CHARACTER;
        const glyph = isChar ? it.char : it.hangul;
        const remove = el('button.btn.btn--ghost', {
          text: '✕', title: 'Remove from deck', onclick: async () => {
            try { await api.deckSetItem(id, it.item_type, it.item_id, false); loadDeck(id); }
            catch (e) { toast(e.message || 'Could not remove', 'error'); }
          },
        });
        return el('div.card.browse-card', {}, [
          el('button.browse-card__glyph.link-btn', { text: glyph, title: 'Open study space', onclick: () => navigate(`#/item/${it.item_type}/${it.item_id}`) }),
          el('div.stack', { style: 'gap:2px' }, [
            el('div.muted', { text: it.meaning_en || '' }),
            !isChar && it.hanja_form ? el('div.faint', { text: it.hanja_form }) : false,
          ]),
          el('div.appbar__spacer'),
          remove,
        ]);
      };

      const actions = el('div.row', {}, [
        el('button.btn.btn--primary', { text: 'Study this deck →', disabled: !items.length, onclick: () => navigate(`#/study/deck/${id}`) }),
        deck && deck.kind !== 'favorite'
          ? el('button.btn.btn--ghost', {
              text: 'Delete deck', onclick: async () => {
                if (!confirm(`Delete the deck “${deck.name}”? Its saved items are removed (the Hanja/words themselves are untouched).`)) return;
                try { await api.deleteDeck(id); toast('Deck deleted', 'info'); navigate('#/decks'); }
                catch (e) { toast(e.message || 'Could not delete', 'error'); }
              },
            })
          : false,
      ]);

      mount(container,
        el('div.row', {}, [el('button.btn.btn--ghost', { text: '← Decks', onclick: () => navigate('#/decks') }), el('div.appbar__spacer')]),
        el('div.card.stack', {}, [
          el('h1', { text: `${deck?.kind === 'favorite' ? '★ ' : ''}${deck?.name || 'Deck'}` }),
          el('div.faint', { text: `${items.length} item${items.length === 1 ? '' : 's'}` }),
          actions,
        ]),
        items.length
          ? el('div.browse-grid', {}, items.map(itemCard))
          : el('div.card', {}, [el('p.muted', { text: 'This deck is empty. Add items with ★ or “Add to deck” from a study space.' })]),
      );
    } catch (e) {
      mount(container, el('div.card.banner.banner--warn', { text: e.message || 'Could not load deck' }));
    }
  }

  if (openDeckId) loadDeck(openDeckId); else loadList();
  return container;
}
